/**
 * @module comments/api
 *
 * Hand-written React Query hooks for the event-comments endpoints. NOT part of
 * the regenerated Orval client — mirrors its shape by hand (same convention as
 * `src/lib/monetization/api.ts`): a `use*Hook` wrapping the request via
 * {@link useCustomInstance} (forwards the player JWT), a `get*QueryKey` helper,
 * and public hooks over `useQuery` / `useInfiniteQuery` / `useMutation`.
 *
 * The lists page with `useInfiniteQuery` over the api's opaque keyset cursor
 * (`nextCursor`), matching the notifications contract.
 *
 * @see src/lib/use-custom-instance.ts — the auth-forwarding axios mutator.
 */
import { useCallback } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';
import type {
  Comment,
  CommentLikeResponse,
  CommentListResponse,
  CommentReportReason,
  CommentsMeResponse,
  CreateCommentBody,
} from './types';

const PAGE_SIZE = 20;

/* ── Query keys ────────────────────────────────────────────────────── */

export const getEventCommentsQueryKey = (eventId: string) => ['comments', 'event', eventId] as const;
export const getCommentRepliesQueryKey = (commentId: string) => ['comments', 'replies', commentId] as const;
export const getCommentsMeQueryKey = (eventId: string) => ['comments', 'me', eventId] as const;

/* ── Top-level list (infinite) ─────────────────────────────────────── */

const useEventCommentsHook = () => {
  const request = useCustomInstance<CommentListResponse>();
  return useCallback(
    (eventId: string, cursor: string | null, signal?: AbortSignal) =>
      request({
        url: `/prediction-market/events/${eventId}/comments`,
        method: 'GET',
        params: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
        signal,
      }),
    [request],
  );
};

/** Newest-first top-level comments for an event, keyset-paged. */
export function useEventComments(eventId: string) {
  const fetchPage = useEventCommentsHook();
  return useInfiniteQuery({
    queryKey: getEventCommentsQueryKey(eventId),
    queryFn: ({ pageParam, signal }) => fetchPage(eventId, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/* ── Replies (infinite, oldest-first) ──────────────────────────────── */

const useCommentRepliesHook = () => {
  const request = useCustomInstance<CommentListResponse>();
  return useCallback(
    (commentId: string, cursor: string | null, signal?: AbortSignal) =>
      request({
        url: `/prediction-market/comments/${commentId}/replies`,
        method: 'GET',
        params: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
        signal,
      }),
    [request],
  );
};

/** Replies under one top-level comment, oldest-first. Enabled on expand. */
export function useCommentReplies(commentId: string, enabled: boolean) {
  const fetchPage = useCommentRepliesHook();
  return useInfiniteQuery({
    queryKey: getCommentRepliesQueryKey(commentId),
    queryFn: ({ pageParam, signal }) => fetchPage(commentId, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

/* ── Caller state (liked ids + ban) ────────────────────────────────── */

const useCommentsMeHook = () => {
  const request = useCustomInstance<CommentsMeResponse>();
  return useCallback(
    (eventId: string, signal?: AbortSignal) =>
      request({ url: `/prediction-market/events/${eventId}/comments/me`, method: 'GET', signal }),
    [request],
  );
};

/** The caller's per-event comment state. Only fetched when signed in. */
export function useCommentsMe<TData = CommentsMeResponse, TError = unknown>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<CommentsMeResponse, TError, TData>> },
): UseQueryResult<TData, TError> {
  const me = useCommentsMeHook();
  const { query } = options ?? {};
  return useQuery({
    queryKey: getCommentsMeQueryKey(eventId),
    queryFn: ({ signal }) => me(eventId, signal),
    ...query,
  });
}

/* ── Post / delete ─────────────────────────────────────────────────── */

interface PostCommentVariables {
  eventId: string;
  data: CreateCommentBody;
}

const usePostCommentHook = () => {
  const request = useCustomInstance<Comment>();
  return useCallback(
    ({ eventId, data }: PostCommentVariables) =>
      request({
        url: `/prediction-market/events/${eventId}/comments`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
      }),
    [request],
  );
};

/** Post a comment or reply; refreshes the event's list (and thread on a reply). */
export function usePostComment<TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Comment, TError, PostCommentVariables, TContext>;
}): UseMutationResult<Comment, TError, PostCommentVariables, TContext> {
  const post = usePostCommentHook();
  const queryClient = useQueryClient();
  const { mutation } = options ?? {};
  return useMutation({
    mutationKey: ['commentsPost'],
    mutationFn: (vars: PostCommentVariables) => post(vars),
    ...mutation,
    onSuccess: (created, vars, ...rest) => {
      void queryClient.invalidateQueries({ queryKey: getEventCommentsQueryKey(vars.eventId) });
      if (created.parentId) {
        void queryClient.invalidateQueries({ queryKey: getCommentRepliesQueryKey(created.parentId) });
      }
      mutation?.onSuccess?.(created, vars, ...rest);
    },
  });
}

interface DeleteCommentVariables {
  eventId: string;
  commentId: string;
  /** Refreshes the thread too when deleting a reply. */
  parentId?: string | null;
}

const useDeleteCommentHook = () => {
  const request = useCustomInstance<void>();
  return useCallback(
    ({ commentId }: DeleteCommentVariables) =>
      request({ url: `/prediction-market/comments/${commentId}`, method: 'DELETE' }),
    [request],
  );
};

/** Soft-delete the caller's own comment. */
export function useDeleteComment<TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<void, TError, DeleteCommentVariables, TContext>;
}): UseMutationResult<void, TError, DeleteCommentVariables, TContext> {
  const del = useDeleteCommentHook();
  const queryClient = useQueryClient();
  const { mutation } = options ?? {};
  return useMutation({
    mutationKey: ['commentsDelete'],
    mutationFn: (vars: DeleteCommentVariables) => del(vars),
    ...mutation,
    onSuccess: (result, vars, ...rest) => {
      void queryClient.invalidateQueries({ queryKey: getEventCommentsQueryKey(vars.eventId) });
      if (vars.parentId) {
        void queryClient.invalidateQueries({ queryKey: getCommentRepliesQueryKey(vars.parentId) });
      }
      mutation?.onSuccess?.(result, vars, ...rest);
    },
  });
}

/* ── Like / unlike (optimistic) ────────────────────────────────────── */

interface LikeVariables {
  eventId: string;
  commentId: string;
  /** True to like, false to unlike. */
  liked: boolean;
  /** Set when liking a reply — its expanded-thread cache is patched/refreshed too. */
  parentId?: string | null;
}

interface LikeContext {
  previousMe: CommentsMeResponse | undefined;
  previousList: InfiniteData<CommentListResponse> | undefined;
  previousThread: InfiniteData<CommentListResponse> | undefined;
}

const useLikeCommentHook = () => {
  const request = useCustomInstance<CommentLikeResponse>();
  return useCallback(
    ({ commentId, liked }: LikeVariables) =>
      request({ url: `/prediction-market/comments/${commentId}/like`, method: liked ? 'PUT' : 'DELETE' }),
    [request],
  );
};

/** Adjust one comment's likeCount across the cached list pages (top-level + embedded replies). */
function adjustLikeCount(data: InfiniteData<CommentListResponse>, commentId: string, delta: number) {
  const patch = (c: Comment): Comment => {
    if (c.id === commentId) return { ...c, likeCount: Math.max(0, c.likeCount + delta) };
    if (c.replies) return { ...c, replies: c.replies.map(patch) };
    return c;
  };
  return { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map(patch) })) };
}

/**
 * Optimistic like toggle: bumps the cached count + liked-ids immediately,
 * rolls back on error, settles from the server response.
 */
export function useToggleCommentLike(): UseMutationResult<CommentLikeResponse, unknown, LikeVariables, LikeContext> {
  const toggle = useLikeCommentHook();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['commentsLike'],
    mutationFn: (vars: LikeVariables) => toggle(vars),
    onMutate: async (vars) => {
      const meKey = getCommentsMeQueryKey(vars.eventId);
      const listKey = getEventCommentsQueryKey(vars.eventId);
      // A liked reply may be rendered from its expanded-thread cache instead of
      // the embedded copy in the top-level list — patch both.
      const threadKey = vars.parentId ? getCommentRepliesQueryKey(vars.parentId) : null;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: meKey }),
        queryClient.cancelQueries({ queryKey: listKey }),
        ...(threadKey ? [queryClient.cancelQueries({ queryKey: threadKey })] : []),
      ]);

      const previousMe = queryClient.getQueryData<CommentsMeResponse>(meKey);
      const previousList = queryClient.getQueryData<InfiniteData<CommentListResponse>>(listKey);
      const previousThread = threadKey ? queryClient.getQueryData<InfiniteData<CommentListResponse>>(threadKey) : undefined;

      if (previousMe) {
        queryClient.setQueryData<CommentsMeResponse>(meKey, {
          ...previousMe,
          likedCommentIds: vars.liked
            ? [...previousMe.likedCommentIds, vars.commentId]
            : previousMe.likedCommentIds.filter((id) => id !== vars.commentId),
        });
      }
      const delta = vars.liked ? 1 : -1;
      if (previousList) {
        queryClient.setQueryData(listKey, adjustLikeCount(previousList, vars.commentId, delta));
      }
      if (threadKey && previousThread) {
        queryClient.setQueryData(threadKey, adjustLikeCount(previousThread, vars.commentId, delta));
      }
      return { previousMe, previousList, previousThread };
    },
    onError: (_error, vars, context) => {
      if (context?.previousMe) queryClient.setQueryData(getCommentsMeQueryKey(vars.eventId), context.previousMe);
      if (context?.previousList) queryClient.setQueryData(getEventCommentsQueryKey(vars.eventId), context.previousList);
      if (vars.parentId && context?.previousThread) {
        queryClient.setQueryData(getCommentRepliesQueryKey(vars.parentId), context.previousThread);
      }
    },
    onSettled: (_result, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: getEventCommentsQueryKey(vars.eventId) });
      void queryClient.invalidateQueries({ queryKey: getCommentsMeQueryKey(vars.eventId) });
      if (vars.parentId) {
        void queryClient.invalidateQueries({ queryKey: getCommentRepliesQueryKey(vars.parentId) });
      }
    },
  });
}

/* ── Report ────────────────────────────────────────────────────────── */

interface ReportVariables {
  commentId: string;
  reason: CommentReportReason;
}

const useReportCommentHook = () => {
  const request = useCustomInstance<void>();
  return useCallback(
    ({ commentId, reason }: ReportVariables) =>
      request({
        url: `/prediction-market/comments/${commentId}/report`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { reason },
      }),
    [request],
  );
};

/** Report a comment to moderation. */
export function useReportComment<TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<void, TError, ReportVariables, TContext>;
}): UseMutationResult<void, TError, ReportVariables, TContext> {
  const report = useReportCommentHook();
  const { mutation } = options ?? {};
  return useMutation({
    mutationKey: ['commentsReport'],
    mutationFn: (vars: ReportVariables) => report(vars),
    ...mutation,
  });
}
